import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PlainPrimaryButton } from 'src/components/Button';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { ButtonBaseType, ID, Maybe } from 'src/models';
import { getUserRoles } from 'src/store/team/team.getters';
import { requestUserRoles } from 'src/store/team/team.actions';
import { makeRoleName } from 'src/tools/string.tools';
import { ResourceData } from 'src/models/modal.model';
import { getCustomIsLoading } from 'src/store/loading/loading.reducer';

import classes from './RoleDropdown.module.scss';

interface Props {
  className?: string;
  value?: ID;
  name?: string;
  onChange: (id: ID, name: string) => void;
  resource: ResourceData;
  isDisabled?: boolean;
}

export default function RoleDropdown({ value, name, onChange, className, resource, isDisabled }: Props) {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);

  const { resource_type, resource_id } = resource;

  const roles = useSelector(getUserRoles)(resource_type, resource_id);

  const isLoading = useSelector(getCustomIsLoading)('roles');

  const items: Maybe<ButtonBaseType[]> = useMemo(() => roles?.map(r => ({ id: r.id, label: r.name })), [roles]);

  const currentItem = useMemo(() => items?.find(r => r.id === value), [items, value]);

  useEffect(() => {
    if (!currentItem && roles?.length) onChange(roles[0].id, roles[0].name);
  }, [currentItem, onChange, roles]);

  useEffect(() => {
    if (isOpen && !roles) dispatch(requestUserRoles(resource_id, resource_type));
  }, [dispatch, isOpen, resource_id, resource_type, roles]);

  function handleOnItemClick(id: ID) {
    const item = roles?.find(r => r.id === id);

    if (item) onChange(item.id, item.name);
  }

  function renderItemLabel(item: ButtonBaseType) {
    return makeRoleName(item.label);
  }

  function renderButton({ isOpen, onClick }: BaseComponentProps) {
    const label = currentItem ? renderItemLabel(currentItem) : makeRoleName(name);

    return (
      <PlainPrimaryButton
        dataTestId='role-button'
        className={classes.button}
        label={label}
        rightIconId={isOpen ? 'chevron-up' : 'chevron-down'}
        onClick={onClick}
        isDisabled={isDisabled}
        isSmall
      />
    );
  }

  return (
    <DropdownMenu
      classNames={{ container: className }}
      renderBaseComponent={renderButton}
      renderItemLabel={renderItemLabel}
      onRequestOpenClose={setIsOpen}
      items={items}
      currentItemId={value}
      onItemClick={handleOnItemClick}
      isLoading={isLoading}
      dropdownMinWidth='16rem'
      isSmall
    />
  );
}
